import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  CreateParticipantInput,
  Participant,
  ParticipantRepository,
  Unsubscribe,
} from '@nx/domain';
import { mapParticipantDoc, snapshotData } from './firestoreMappers';

function participantsCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'participants');
}

export class FirebaseParticipantRepository implements ParticipantRepository {
  constructor(private readonly db: Firestore) {}

  async listByEvent(eventId: string): Promise<Participant[]> {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        participantsCol(this.db, eventId),
        (snap) => {
          unsub();
          resolve(snap.docs.map((d) => mapParticipantDoc(eventId, d.id, snapshotData(d))));
        },
        reject,
      );
    });
  }

  watchByEvent(eventId: string, listener: (participants: Participant[]) => void): Unsubscribe {
    return onSnapshot(participantsCol(this.db, eventId), (snap) => {
      listener(snap.docs.map((d) => mapParticipantDoc(eventId, d.id, snapshotData(d))));
    });
  }

  async create(eventId: string, input: CreateParticipantInput): Promise<Participant> {
    const ref = await addDoc(participantsCol(this.db, eventId), {
      name: input.name.trim(),
      email: input.email.trim().toLowerCase(),
      company: input.company?.trim() ?? null,
      jobTitle: input.jobTitle?.trim() ?? null,
      phone: input.phone?.trim() ?? null,
      tags: input.tags ?? [],
      status: input.status ?? 'pending',
      checkedIn: false,
      checkedInAt: null,
      inviteEmailSentAt: null,
      source: input.source ?? 'manual',
      uid: null,
      customFields: {},
      registeredAt: serverTimestamp(),
    });
    const list = await this.listByEvent(eventId);
    const created = list.find((p) => p.id === ref.id);
    if (!created) throw new Error('Participant not found after create');
    return created;
  }

  async update(
    eventId: string,
    participantId: string,
    input: Partial<CreateParticipantInput> & { checkedIn?: boolean; status?: Participant['status'] },
  ): Promise<Participant> {
    const patch: Record<string, unknown> = {};
    if (input.name !== undefined) patch.name = input.name.trim();
    if (input.email !== undefined) patch.email = input.email.trim().toLowerCase();
    if (input.company !== undefined) patch.company = input.company?.trim() ?? null;
    if (input.jobTitle !== undefined) patch.jobTitle = input.jobTitle?.trim() ?? null;
    if (input.phone !== undefined) patch.phone = input.phone?.trim() ?? null;
    if (input.tags !== undefined) patch.tags = input.tags;
    if (input.status !== undefined) patch.status = input.status;
    if (input.checkedIn !== undefined) {
      patch.checkedIn = input.checkedIn;
      patch.checkedInAt = input.checkedIn ? serverTimestamp() : null;
    }
    await updateDoc(doc(this.db, 'events', eventId, 'participants', participantId), patch);
    const list = await this.listByEvent(eventId);
    const updated = list.find((p) => p.id === participantId);
    if (!updated) throw new Error('Participant not found after update');
    return updated;
  }

  async delete(eventId: string, participantId: string): Promise<void> {
    await deleteDoc(doc(this.db, 'events', eventId, 'participants', participantId));
  }

  async batchCreate(eventId: string, inputs: CreateParticipantInput[]): Promise<number> {
    const batch = writeBatch(this.db);
    let count = 0;
    for (const input of inputs) {
      const ref = doc(participantsCol(this.db, eventId));
      batch.set(ref, {
        name: input.name.trim(),
        email: input.email.trim().toLowerCase(),
        company: input.company?.trim() ?? null,
        jobTitle: input.jobTitle?.trim() ?? null,
        phone: input.phone?.trim() ?? null,
        tags: input.tags ?? [],
        status: input.status ?? 'pending',
        checkedIn: false,
        checkedInAt: null,
        inviteEmailSentAt: null,
        source: input.source ?? 'csv_import',
        uid: null,
        customFields: {},
        registeredAt: serverTimestamp(),
      });
      count += 1;
    }
    await batch.commit();
    return count;
  }

  async getRegistrationCount(eventId: string): Promise<number> {
    const counterRef = doc(this.db, 'events', eventId, '_counters', 'registration');
    const snap = await getDoc(counterRef);
    if (!snap.exists()) return 0;
    const data = snap.data();
    return typeof data?.count === 'number' ? data.count : 0;
  }
}
